import { KB_CATEGORIES, type KbDocument, type SaveKbDocument } from '@dnx/contracts';
import { Alert, Badge, Button, Card, cn, Field, Input, Select } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { BookOpenCheck, FilePlus2, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';

const CATEGORY: Record<string, string> = { procedures: 'Procedimentos', care: 'Cuidados e resultado', pricing: 'Valores', clinic: 'A clínica', faq: 'Dúvidas frequentes' };
const fmt = (iso: string) => new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(iso));

/**
 * Base de conhecimento do assistente virtual (docs/01 §11.3): o que a IA pode dizer aos pacientes.
 * Regra de compliance: só conteúdo APROVADO por médico entra na IA; editar volta para rascunho.
 */
export function KbPage() {
  const qc = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('ai.kb.write');
  const canApprove = me.permissions.includes('ai.kb.approve');
  const { data: docs } = useQuery({ queryKey: ['ai', 'kb'], queryFn: () => api<KbDocument[]>('/ai/kb') });
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const selected = selectedId === 'new' ? null : docs?.find((d) => d.id === selectedId) ?? null;
  const [form, setForm] = useState<SaveKbDocument>({ title: '', category: 'faq', body: '' });
  const [confirmApprove, setConfirmApprove] = useState(false);
  // Recarrega o formulário só ao trocar de documento ou quando a versão muda (salvar/aprovar) —
  // nunca a cada nova busca dos dados, para não apagar o que a pessoa está digitando.
  const loadedKey = useRef('');
  useEffect(() => {
    const key = `${selectedId}:${selected?.version ?? ''}:${selected?.status ?? ''}`;
    if (key === loadedKey.current) return;
    loadedKey.current = key;
    setConfirmApprove(false);
    if (selectedId === 'new') setForm({ title: '', category: 'faq', body: '' });
    else if (selected) setForm({ title: selected.title, category: selected.category, body: selected.body });
  }, [selectedId, selected]);
  useEffect(() => { if (!selectedId && docs?.length) setSelectedId(docs[0]!.id); }, [docs, selectedId]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['ai'] });
  const save = useMutation({
    mutationFn: async () => {
      if (selectedId === 'new') return (await api<{ id: string }>('/ai/kb', { method: 'POST', body: form })).id;
      await api(`/ai/kb/${selectedId}`, { method: 'PUT', body: form });
      return selectedId!;
    },
    onSuccess: async (id) => { await refresh(); setSelectedId(id); },
  });
  const approve = useMutation({
    mutationFn: () => api(`/ai/kb/${selectedId}/approve`, { method: 'POST' }),
    onSuccess: () => { setConfirmApprove(false); void refresh(); },
  });
  const archive = useMutation({
    mutationFn: () => api(`/ai/kb/${selectedId}/archive`, { method: 'POST' }),
    onSuccess: () => { setSelectedId(null); void refresh(); },
  });

  const dirty = !!selected && (form.title !== selected.title || form.category !== selected.category || form.body !== selected.body);
  const approved = docs?.filter((d) => d.status === 'approved').length ?? 0;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">Base de conhecimento</h1>
          <p className="mt-1 text-sm text-fg-muted">O que o assistente virtual pode responder aos pacientes. {approved} de {docs?.length ?? 0} documentos aprovados.</p>
        </div>
        {canWrite && <Button variant="secondary" onClick={() => setSelectedId('new')}><FilePlus2 className="size-4" />Novo documento</Button>}
      </div>
      <Alert tone="info" className="mt-5" title="Só conteúdo aprovado por médico chega ao paciente">
        O assistente responde apenas com o que está aprovado aqui — nunca com conhecimento próprio. Editar um documento aprovado
        o tira do assistente até nova aprovação. Não inclua valores em reais nem promessas de resultado.
      </Alert>

      <div className="mt-5 grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <Card className="overflow-hidden">
          {KB_CATEGORIES.map((cat) => {
            const items = docs?.filter((d) => d.category === cat) ?? [];
            if (!items.length) return null;
            return (
              <div key={cat}>
                <p className="border-b border-border bg-surface-muted/60 px-4 py-2 text-[11.5px] font-semibold tracking-wide text-fg-muted uppercase">{CATEGORY[cat]}</p>
                <ul>
                  {items.map((d) => (
                    <li key={d.id}>
                      <button type="button" onClick={() => setSelectedId(d.id)}
                        className={cn('flex w-full items-center justify-between gap-2 border-b border-border px-4 py-2.5 text-left text-[13px] hover:bg-surface-muted/60',
                          d.id === selectedId && 'bg-primary-soft hover:bg-primary-soft')}>
                        <span className="min-w-0 truncate font-medium text-fg">{d.title}</span>
                        <Badge tone={d.status === 'approved' ? 'success' : 'warning'}>{d.status === 'approved' ? 'Aprovado' : 'Rascunho'}</Badge>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {docs?.length === 0 && <p className="p-6 text-center text-sm text-fg-muted">Nenhum documento ainda.</p>}
        </Card>

        {(selected || selectedId === 'new') && (
          <Card className="p-5">
            <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
              <div className="flex flex-wrap items-center gap-2">
                {selected?.status === 'approved'
                  ? <Badge tone="success"><ShieldCheck className="size-3" />Aprovado por {selected.approvedBy?.name} em {fmt(selected.approvedAt!)} · versão {selected.version}</Badge>
                  : selected ? <Badge tone="warning">Rascunho · versão {selected.version} — ainda não usado pelo assistente</Badge> : <Badge>Novo</Badge>}
              </div>
              <div className="grid gap-4 sm:grid-cols-[1fr_220px]">
                <Field label="Título"><Input value={form.title} disabled={!canWrite} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
                <Field label="Categoria">
                  <Select value={form.category} disabled={!canWrite} onChange={(e) => setForm({ ...form, category: e.target.value as SaveKbDocument['category'] })}>
                    {KB_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY[c]}</option>)}
                  </Select>
                </Field>
              </div>
              <Field label="Conteúdo" hint="Linguagem simples, como explicaria a um paciente. Parágrafos curtos.">
                <textarea value={form.body} disabled={!canWrite} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={16}
                  className="w-full rounded-md border border-border-strong bg-surface px-3 py-2.5 text-sm leading-relaxed text-fg focus:border-primary disabled:bg-surface-muted" />
              </Field>
              {dirty && selected?.status === 'approved' && <Alert tone="warning">Salvar volta este documento para rascunho: ele sai do assistente até um médico aprovar de novo.</Alert>}
              {(save.error || approve.error || archive.error) && <Alert tone="danger">{(save.error ?? approve.error ?? archive.error)!.message}</Alert>}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
                <div>{canWrite && selected && <Button variant="ghost" size="sm" onClick={() => archive.mutate()} loading={archive.isPending}>Arquivar</Button>}</div>
                <div className="flex gap-2">
                  {canWrite && <Button type="submit" variant="secondary" loading={save.isPending} disabled={selectedId !== 'new' && !dirty}>Salvar rascunho</Button>}
                  {canApprove && selected && selected.status !== 'approved' && !dirty && !confirmApprove && (
                    <Button onClick={() => setConfirmApprove(true)}><BookOpenCheck className="size-4" />Aprovar para o assistente</Button>
                  )}
                </div>
              </div>
              {confirmApprove && (
                <Alert tone="warning" title="Aprovação médica">
                  <p>Revisei este conteúdo e autorizo o assistente virtual a usá-lo nas respostas aos pacientes. A aprovação fica registrada com meu nome.</p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate()}>Confirmar aprovação</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmApprove(false)}>Voltar</Button>
                  </div>
                </Alert>
              )}
              {!canApprove && selected?.status !== 'approved' && selected && (
                <p className="text-[12.5px] text-fg-muted">A aprovação é feita por um médico da clínica.</p>
              )}
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
